import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import SVG from 'react-inlinesvg';
import { sideScroll } from 'lib/helpers/helpers';
import Link from 'components/Link';
import {
  PickerStyle,
  PickerCarouselStyle,
  PickerCarouselItemsStyle,
  PickerItemStyle,
  PrevArrowStyle,
  NextArrowStyle
} from './Picker.style';
import { ICONS } from '../../constants/icons';

const itemsToScroll = 4;
const defaultRangeToScroll = 100;
const scrollOffset = 5;

const Picker: FunctionComponent<IPickerProps> = ({
  items,
  selectedItem,
  isGenrePicker,
  handlePickerItemClick
}): JSX.Element => {
  const pickerCarouselRef = useRef<any>(null);
  const prevArrowRef = useRef<HTMLDivElement>(null);
  const nextArrowRef = useRef<HTMLDivElement>(null);
  const [rangeToScroll, setRangeToScroll] = useState<number>(defaultRangeToScroll);

  useEffect(() => {
    setScrollingRange();
    updateArrows();
    pickerCarouselRef.current.onscroll = updateArrows;
    window.addEventListener('resize', setScrollingRange);

    return () => {
      window.removeEventListener('resize', setScrollingRange);
    };
  }, []);

  /*
   * Set scroll range by specified amount of items (itemsToScroll).
   * If total available screen width is less than specified scroll offset,
   * then scroll range will be set to half of that width.
   */
  const setScrollingRange = (): void => {
    const containerWidth: number = pickerCarouselRef.current.offsetWidth;
    if (pickerCarouselRef.current.children[0] && pickerCarouselRef.current.children[0].offsetWidth) {
      if (containerWidth <= pickerCarouselRef.current.children[0].offsetWidth * itemsToScroll) {
        setRangeToScroll(containerWidth / 2);
      } else {
        setRangeToScroll(pickerCarouselRef.current.children[0].offsetWidth * (itemsToScroll - 1));
      }
    }
    updateArrows();
  };

  const updateArrows = (): void => {
    if (pickerCarouselRef.current) {
      const showPrev: boolean = pickerCarouselRef.current.scrollLeft > scrollOffset;
      setVisible(prevArrowRef.current, showPrev);

      const showNext: boolean =
        pickerCarouselRef.current.scrollLeft <
        pickerCarouselRef.current.scrollWidth - pickerCarouselRef.current.offsetWidth - scrollOffset;
      setVisible(nextArrowRef.current, showNext);
    }
  };

  const setVisible = (element: HTMLDivElement | null, visible: boolean = true): void => {
    element ? (element.style.display = visible ? 'block' : 'none') : null;
  };

  const prevArrowClickHandler = (): void => {
    sideScroll(pickerCarouselRef.current, -rangeToScroll);
  };

  const nextArrowClickHandler = (): void => {
    sideScroll(pickerCarouselRef.current, rangeToScroll);
  };

  return (
    <PickerStyle data-testid="picker">
      <PickerCarouselStyle data-testid="pickerCarouselList">
        <PickerCarouselItemsStyle ref={pickerCarouselRef}>
          {items.map(item => (
            <Link path={!item.isDisabled && item.path} key={item.label}>
              <PickerItemStyle
                data-testid="pickerItemStyle"
                isSelected={selectedItem === item.label}
                isDisabled={item.isDisabled}
                isGenrePicker={isGenrePicker}
                onClick={() => !item.isDisabled && handlePickerItemClick(item.label)}
              >
                {item.label}
              </PickerItemStyle>
            </Link>
          ))}
        </PickerCarouselItemsStyle>
        <PrevArrowStyle data-testid="prevArrowStyle" ref={prevArrowRef} onClick={prevArrowClickHandler}>
          <SVG src={ICONS.ARROW_SMALL} />
        </PrevArrowStyle>
        <NextArrowStyle data-testid="nextArrowStyle" ref={nextArrowRef} onClick={nextArrowClickHandler}>
          <SVG src={ICONS.ARROW_SMALL} />
        </NextArrowStyle>
      </PickerCarouselStyle>
    </PickerStyle>
  );
};

export default Picker;
